import React, { useState, useEffect, useMemo } from "react";
import apiClient from "../../../../../api/apiClient";
import Loading from "../../../../../components/Loading/Loading";
import CampaignStatusBadge from "./components/CampaignStatusBadge";

const ORDINAL = ["1st", "2nd", "3rd", "4th", "5th", "6th", "7th", "8th"];
const ord = (n) => ORDINAL[n] || `${n + 1}th`;

const STATUS_TABS = ["all", "active", "completed", "replied", "skipped"];
const STATUS_LABEL = { all: "All", active: "Active", completed: "Completed", replied: "Replied", skipped: "Skipped" };

const CampaignAnalytics = ({ campaignId, onBack }) => {
    const [campaign, setCampaign]           = useState(null);
    const [campaignSteps, setCampaignSteps] = useState([]);
    const [analytics, setAnalytics]         = useState(null);
    const [leads, setLeads]                 = useState([]);
    const [leadsTotal, setLeadsTotal]       = useState(0);
    const [loading, setLoading]             = useState(true);
    const [error, setError]                 = useState("");
    const [activeTab, setActiveTab]         = useState("all");
    
    useEffect(() => {
        setLoading(true);
        Promise.all([
            apiClient.get(`/admin/crm/campaigns/${campaignId}`),
            apiClient.get(`/admin/crm/campaigns/${campaignId}/analytics`),
            apiClient.get(`/admin/crm/campaigns/${campaignId}/leads?limit=100`),
        ])
            .then(([cRes, aRes, lRes]) => {
                setCampaign(cRes.data.campaign);
                setCampaignSteps(cRes.data.steps || []);
                setAnalytics(aRes.data);
                setLeads(lRes.data.leads);
                setLeadsTotal(lRes.data.total);
            })
            .catch(() => setError("Failed to load analytics"))
            .finally(() => setLoading(false));
    }, [campaignId]);

    // Group analytics steps into threads
    const threadGroups = useMemo(() => {
        if (!analytics) return [];
        const groups = [];
        let cur = { subject: campaign?.subject || "", items: [] };

        analytics.steps.forEach((s) => {
            if (s.type === "thread") {
                groups.push(cur);
                cur = { subject: s.subject || "", items: [] };
            } else {
                cur.items.push(s);
            }
        });
        groups.push(cur);

        return groups
            .filter((g) => g.items.some((s) => s.type === "email" || s.type === "delay"))
            .map((g) => {
                const emails = g.items.filter((s) => s.type === "email");
                const totalSent   = emails.reduce((a, s) => a + (s.sent   || 0), 0);
                const totalOpened = emails.reduce((a, s) => a + (s.opened || 0), 0);
                return {
                    ...g,
                    emails,
                    totalSent,
                    totalOpened,
                    openRate: totalSent > 0 ? Math.round((totalOpened / totalSent) * 100) : 0,
                };
            });
    }, [analytics, campaign]);

    // Global step numbers (email + delay, not thread)
    const stepNum = useMemo(() => {
        if (!analytics) return {};
        const map = {};
        let n = 0;
        analytics.steps.forEach((s) => { if (s.type !== "thread") map[s.stepId] = ++n; });
        return map;
    }, [analytics]);

    const totalNonThread = useMemo(
        () => (campaignSteps || []).filter((s) => s.type !== "thread").length,
        [campaignSteps]
    );

    const statusCounts = useMemo(() => {
        const c = { all: leads.length };
        leads.forEach((l) => { c[l.enrollmentStatus] = (c[l.enrollmentStatus] || 0) + 1; });
        return c;
    }, [leads]);

    const visibleLeads = useMemo(
        () => activeTab === "all" ? leads : leads.filter((l) => l.enrollmentStatus === activeTab),
        [leads, activeTab]
    );

    if (loading) return <div className="admin-loading-wrapper"><Loading /></div>;
    if (error)   return <div className="admin-form-error" style={{ padding: "var(--space-5)" }}>{error}</div>;

    const { summary } = analytics;
    
    return (
        <div className="ca-root">

            {/* ── Header ── */}
            <div className="ca-header">
                <div className="ca-header__left">
                    <button className="ca-header__back" onClick={onBack} type="button">
                        ← Campaigns
                    </button>
                    <h2 className="ca-header__title">{campaign.name}</h2>
                    {campaign.subject && (
                        <span className="ca-header__subject">{campaign.subject}</span>
                    )}
                </div>
                <CampaignStatusBadge status={campaign.status} />
            </div>

            {/* ── Four key metrics ── */}
            <div className="ca-metrics">
                <div className="ca-metric">
                    <div className="ca-metric__val">{summary.totalEnrolled}</div>
                    <div className="ca-metric__key">Enrolled</div>
                </div>
                <div className="ca-metric">
                    <div className="ca-metric__val">{summary.totalSent}</div>
                    <div className="ca-metric__key">Emails Sent</div>
                </div>
                <div className="ca-metric">
                    <div className="ca-metric__val">{summary.openRate}%</div>
                    <div className="ca-metric__key">Open Rate</div>
                    <div className="ca-metric__sub">{summary.totalOpened} of {summary.totalSent} opened</div>
                    <div className="ca-metric__bar">
                        <div className="ca-metric__bar-fill" style={{ width: `${summary.openRate}%` }} />
                    </div>
                </div>
                <div className="ca-metric">
                    <div className="ca-metric__val">{summary.replyRate}%</div>
                    <div className="ca-metric__key">Reply Rate</div>
                    <div className="ca-metric__sub">{summary.totalReplied} of {summary.totalSent} replied</div>
                    <div className="ca-metric__bar">
                        <div className="ca-metric__bar-fill" style={{ width: `${summary.replyRate}%` }} />
                    </div>
                </div>
            </div>

            {/* ── Sequence performance ── */}
            {threadGroups.length > 0 && (
                <div className="ca-section">
                    <div className="ca-section-label">Sequence Performance</div>
                    <div className="ca-threads">
                        {threadGroups.map((group, gi) => (
                            <div key={gi} className="ca-thread">
                                <div className="ca-thread__head">
                                    <span className="ca-thread__id">{ord(gi)} Thread</span>
                                    <span className="ca-thread__subj">{group.subject || "(no subject)"}</span>
                                    {group.totalSent > 0 && (
                                        <span className="ca-thread__total">
                                            <strong>{group.openRate}%</strong> open · {group.totalSent} sent
                                        </span>
                                    )}
                                </div>
                                <div className="ca-steps">
                                    {group.items.map((s, si) => {
                                        if (s.type === "delay") {
                                            return (
                                                <div key={s.stepId ?? `d${si}`} className="ca-delay">
                                                    <span className="ca-delay__text">↓ wait {s.delayDays ?? "?"} day{s.delayDays !== 1 ? "s" : ""}</span>
                                                </div>
                                            );
                                        }
                                        return (
                                            <div key={s.stepId} className="ca-step">
                                                <span className="ca-step__num">{stepNum[s.stepId] ?? si + 1}</span>
                                                <span className="ca-step__label">Email</span>
                                                <div className="ca-step__data">
                                                    <div className="ca-step__count">
                                                        <span className="ca-step__count-val">{s.sent}</span>
                                                        <span className="ca-step__count-key">Sent</span>
                                                    </div>
                                                    <div className="ca-step__count">
                                                        <span className="ca-step__count-val">{s.opened}</span>
                                                        <span className="ca-step__count-key">Opened</span>
                                                    </div>
                                                    <div className="ca-step__bar-wrap">
                                                        <div className="ca-step__bar-track">
                                                            <div className="ca-step__bar-fill" style={{ width: `${s.openRate}%` }} />
                                                        </div>
                                                        <span className="ca-step__pct">{s.sent > 0 ? `${s.openRate}%` : "—"}</span>
                                                    </div>
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {/* ── Enrolled leads ── */}
            <div className="ca-section">
                <div className="ca-section-label ca-section-label--row">
                    <span>Enrolled Leads</span>
                    <span style={{ fontWeight: 400 }}>{leadsTotal} total</span>
                </div>

                {/* Underline tabs */}
                <div className="ca-tabs">
                    {STATUS_TABS.filter((t) => t === "all" || statusCounts[t]).map((t) => (
                        <button
                            key={t}
                            type="button"
                            className={`ca-tab${activeTab === t ? " ca-tab--active" : ""}`}
                            onClick={() => setActiveTab(t)}
                        >
                            {STATUS_LABEL[t]}
                            <span className="ca-tab__n">{statusCounts[t] ?? 0}</span>
                        </button>
                    ))}
                </div>

                {visibleLeads.length === 0 ? (
                    <div className="ca-empty">No leads with this status.</div>
                ) : (
                    <div className="admin-table-scroll">
                        <table className="crm-lead-table">
                            <thead>
                                <tr>
                                    <th>Name</th>
                                    <th>Email</th>
                                    <th>Company</th>
                                    <th>Status</th>
                                    <th>Progress</th>
                                    <th>Last Sent</th>
                                    <th>Enrolled</th>
                                </tr>
                            </thead>
                            <tbody>
                                {visibleLeads.map((l) => {
                                    const done = (campaignSteps || [])
                                        .slice(0, l.currentStepIndex)
                                        .filter((s) => s.type !== "thread").length;
                                    const pct = totalNonThread > 0 ? Math.round((done / totalNonThread) * 100) : 0;
                                    return (
                                        <tr key={l.enrollmentId}>
                                            <td style={{ fontWeight: 500 }}>{l.firstName} {l.lastName}</td>
                                            <td style={{ color: "var(--color-text-secondary)" }}>{l.email}</td>
                                            <td style={{ color: "var(--color-text-secondary)" }}>{l.company || "—"}</td>
                                            <td>
                                                <span className={`ca-dot ca-dot--${l.enrollmentStatus}`}>
                                                    {l.enrollmentStatus}
                                                </span>
                                            </td>
                                            <td>
                                                {totalNonThread > 0 ? (
                                                    <div className="ca-prog">
                                                        <div className="ca-prog__track">
                                                            <div className="ca-prog__fill" style={{ width: `${pct}%` }} />
                                                        </div>
                                                        <span className="ca-prog__label">{done}/{totalNonThread}</span>
                                                    </div>
                                                ) : "—"}
                                            </td>
                                            <td style={{ color: "var(--color-text-secondary)" }}>
                                                {l.lastSentAt ? new Date(l.lastSentAt).toLocaleDateString() : "—"}
                                            </td>
                                            <td style={{ color: "var(--color-text-secondary)" }}>
                                                {new Date(l.enrolledAt).toLocaleDateString()}
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                )}
                {leadsTotal > leads.length && (
                    <div className="ca-overflow">Showing {leads.length} of {leadsTotal}</div>
                )}
            </div>

        </div>
    );
};

export default CampaignAnalytics;
